import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  LayoutDashboard, Bell, List, Columns3,
  LogOut, PanelLeftClose, PanelLeftOpen, Sun, Moon,
  Settings, Map, Bot, FileText, BarChart3, HelpCircle, Users, Activity,
  Inbox
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import WelcomeModal from './WelcomeModal';

// `requester: true` means the item is visible to the minimal requester role too.
// Everything else is hidden from requesters.
// `roles` limits an item to the listed roles. Intake reviewers see only the
// items that list 'intake_reviewer'.
const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  // One register for every role. New requests start from its "+ New request"
  // button; New Intake, New Use Case and My Requests left the nav (2026-10-21).
  { path: '/intake-requests', icon: Inbox, label: 'Intake requests', requester: true,
    roles: ['admin', 'manager', 'pm', 'viewer', 'requester', 'intake_reviewer'] },
  { path: '/approvals', icon: Bell, label: 'Approvals', badge: true },
  { path: '/backlog', icon: List, label: 'Backlog' },
  { path: '/sprint-board', icon: Columns3, label: 'Sprint Board' },
  { path: '/roadmap', icon: Map, label: 'Roadmap' },
  { path: '/reports', icon: FileText, label: 'Reports' },
  { path: '/metrics', icon: BarChart3, label: 'Metrics' },
  { path: '/settings/agents', icon: Bot, label: 'AI Agents' },
  { path: '/settings/notifications', icon: Bell, label: 'Notifications' },
  { path: '/settings/observability', icon: Activity, label: 'Observability', adminOnly: true },
  { path: '/settings/users', icon: Users, label: 'Users', adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const isRequester = user?.role === 'requester';
  const isIntakeReviewer = user?.role === 'intake_reviewer';
  // Scoped roles get no approvals badge, settings or notifications chrome.
  const isScoped = isRequester || isIntakeReviewer;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('pilot_sidebar') === 'collapsed');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { localStorage.setItem('pilot_sidebar', collapsed ? 'collapsed' : 'expanded'); }, [collapsed]);

  useEffect(() => {
    if (isScoped) return undefined; // Scoped roles don't see approvals.
    const fetchPending = async () => {
      try { const res = await api.get('/approvals/', { params: { status: 'pending' } }); setPendingCount(res.data.length); } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [isScoped]);

  useEffect(() => {
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  const sideW = collapsed ? 'w-[72px]' : 'w-64';
  const mainMl = collapsed ? 'ml-[72px]' : 'ml-64';
  const [showWelcome, setShowWelcome] = useState(false);

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      {/* Welcome/Onboarding Modal */}
      {/* The tour covers the full app, so intake reviewers do not get it. */}
      {!isIntakeReviewer && <WelcomeModal forceShow={showWelcome} onClose={() => setShowWelcome(false)} />}
      
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 z-40 h-screen ${sideW} bg-white dark:bg-[#1a1a2e] border-r border-[#e3e3e3] dark:border-white/10 flex flex-col transition-all duration-300 ease-in-out`} data-testid="sidebar">
        {/* Logo */}
        <div className="px-4 pt-5 pb-4 border-b border-[#e3e3e3] dark:border-white/10">
          {collapsed ? (
            <div className="h-7 flex items-center justify-center">
              <img src="/advania-icon.png" alt="Advania" className="h-7 w-auto object-contain" data-testid="advania-icon" />
            </div>
          ) : (
            <>
              <div className="h-14 flex items-center">
                <img src={dark ? '/advania-logo-white.png' : '/advania-logo.png'} alt="Advania" className="h-14 w-auto" data-testid="advania-logo" />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Speedforce</span>
                <span className="text-[11px] text-[#30302F] dark:text-white">|</span>
                <span className="text-[11px] text-[#30302F] dark:text-white font-light">PM Co-Pilot</span>
              </div>
            </>
          )}
        </div>

        {/* Nav */}
        <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-3'} pt-4 space-y-0.5`} data-testid="sidebar-nav" aria-label="Main navigation">
          {navItems
            .filter(item => !item.adminOnly || user?.role === 'admin')
            .filter(item => !isRequester || item.requester)
            .filter(item => !item.roles || item.roles.includes(user?.role))
            .filter(item => !isIntakeReviewer || item.roles?.includes('intake_reviewer'))
            .map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              aria-label={item.label}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `relative flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-2' : 'px-3'} py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] border-l-[3px] border-l-[#98139C]'
                    : 'text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 hover:text-[#30302F] dark:hover:text-white border-l-[3px] border-l-transparent'
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.5} />
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {item.badge && pendingCount > 0 && (
                <span className={`bg-[#98139C] text-white text-[11px] font-bold rounded-full flex items-center justify-center ${collapsed ? 'absolute -top-0.5 -right-0.5 w-4 h-4 text-[8px]' : 'w-5 h-5'}`}>
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar footer — collapse only */}
        <div className={`${collapsed ? 'px-2' : 'px-4'} py-3 border-t border-[#e3e3e3] dark:border-white/10`}>
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`flex items-center ${collapsed ? 'justify-center' : ''} gap-2 text-sm text-[#30302F] dark:text-white hover:text-[#98139C] dark:hover:text-[#d87bdb] transition-colors w-full ${collapsed ? 'px-0 py-2' : 'px-1 py-1.5'}`}
          >
            {collapsed ? <PanelLeftOpen size={16} strokeWidth={1.5} /> : <PanelLeftClose size={16} strokeWidth={1.5} />}
            {!collapsed && <span className="font-light">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main content — min-w-0 stops the flex column from expanding past the
          viewport when a wide table/grid lives inside, so the page never
          horizontal-scrolls (only the table does). Without it the sticky
          header slides off when the user scrolls right. */}
      <div className={`${mainMl} flex-1 min-w-0 min-h-screen bg-white dark:bg-[#0f0f23] transition-all duration-300 ease-in-out flex flex-col`}>
        {/* Top header bar */}
        <header className="sticky top-0 z-30 h-14 bg-white dark:bg-[#0f0f23] border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-end px-6 gap-2" data-testid="top-header" aria-label="Top header">
          {/* Theme toggle */}
          <button data-testid="theme-toggle" onClick={toggle} title={dark ? 'Light mode' : 'Dark mode'} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} className="w-9 h-9 flex items-center justify-center text-[#30302F] dark:text-white hover:text-[#98139C] dark:hover:text-[#d87bdb] hover:bg-[#faf8fb] dark:hover:bg-white/5 transition-colors">
            {dark ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
          </button>

          {/* Notifications: hidden for scoped roles (no approvals access) */}
          {!isScoped && (
            <button data-testid="header-notifications" onClick={() => navigate('/approvals')} aria-label={pendingCount > 0 ? `${pendingCount} pending approvals` : 'No pending approvals'} title="Notifications" className="relative w-9 h-9 flex items-center justify-center text-[#30302F] dark:text-white hover:text-[#98139C] dark:hover:text-[#d87bdb] hover:bg-[#faf8fb] dark:hover:bg-white/5 transition-colors">
              <Bell size={18} strokeWidth={1.5} />
              {pendingCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#98139C] text-white text-[8px] font-bold rounded-full flex items-center justify-center">{pendingCount}</span>
              )}
            </button>
          )}

          {/* Settings: hidden for scoped roles */}
          {!isScoped && (
            <button
              data-testid="header-settings"
              onClick={() => navigate('/settings/ado')}
              className="w-9 h-9 flex items-center justify-center text-[#30302F] dark:text-white hover:text-[#98139C] dark:hover:text-[#d87bdb] hover:bg-[#faf8fb] dark:hover:bg-white/5 transition-colors"
              title="Azure DevOps Settings"
              aria-label="Azure DevOps Settings"
            >
              <Settings size={18} strokeWidth={1.5} />
            </button>
          )}

          {/* Separator */}
          <div className="w-px h-6 bg-[#e3e3e3] dark:bg-white/10 mx-1" />

          {/* User dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              data-testid="user-menu-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label="User menu"
              aria-haspopup="true"
              aria-expanded={userMenuOpen}
              className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-[#faf8fb] dark:hover:bg-white/5 transition-colors"
            >
              <div className="w-8 h-8 bg-[#f0dcf0] dark:bg-[#98139C]/20 rounded-full flex items-center justify-center text-xs font-semibold text-[#4F0077] dark:text-[#d87bdb]">
                {initials}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-medium text-[#30302F] dark:text-white leading-tight">{user?.name}</div>
                <div className="text-[11px] text-[#30302F] dark:text-white capitalize leading-tight">{user?.role?.replace('_', ' ')}</div>
              </div>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 shadow-lg py-1 z-50" data-testid="user-dropdown" role="menu" aria-label="User actions">
                <div className="px-3 py-2 border-b border-[#e3e3e3] dark:border-white/10 sm:hidden">
                  <div className="text-sm font-medium text-[#30302F] dark:text-white">{user?.name}</div>
                  <div className="text-[11px] text-[#30302F] dark:text-white capitalize">{user?.role?.replace('_', ' ')}</div>
                </div>
                {!isScoped && (
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/settings/ado'); }}
                    className="w-full text-left px-3 py-2 text-sm text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 flex items-center gap-2"
                  >
                    <Settings size={14} /> Settings
                  </button>
                )}
                {!isIntakeReviewer && (
                  <button
                    onClick={() => { setUserMenuOpen(false); setShowWelcome(true); }}
                    className="w-full text-left px-3 py-2 text-sm text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 flex items-center gap-2"
                  >
                    <HelpCircle size={14} /> Quick Tour
                  </button>
                )}
                <button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-sm text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 flex items-center gap-2"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content — min-w-0 lets the inner content shrink so any wide
            child uses overflow-x-auto to scroll inside itself. */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
